import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { readEnv } from '@/env';
import type { NarrativeAnnotation } from '@/features/patient-record/api/textMining';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useFeatureMutations } from '@/features/patient-record/hooks/useFeatureMutations';
import { useMedicalHistoryMutations } from '@/features/patient-record/hooks/useMedicalHistoryMutations';
import { defaultFeatureInput } from '@/features/patient-record/lib/featureForm';
import type { MedicalHistoryViewModel } from '@/features/patient-record/types';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from '../_shared/CollapsibleCard';
import { DraftFooter } from '../_shared/DraftFooter';

import { NarrativeWithSuggestions } from './NarrativeWithSuggestions';
import { PhiHintLabel } from './PhiHintLabel';

interface NarrativeCardProps {
  readonly guid: string;
  readonly history: MedicalHistoryViewModel;
}

interface NarrativeDraft {
  readonly indicationForReferral: string;
  readonly medicalAndDevelopmentalHistory: string;
}

/**
 * Two free-text fields — indication for referral, medical &
 * developmental history. Edits are held as a draft and committed
 * explicitly via the Save button so accidental edits don't persist.
 */
export function NarrativeCard({ guid, history }: NarrativeCardProps): ReactElement {
  const { t } = useTranslation();
  const { updateNarrative } = useMedicalHistoryMutations(guid);
  const { create: createFeature } = useFeatureMutations(guid);
  // PTV2-1051 #5: narrative NLP is gated by a runtime env flag because the
  // BE Text Mining endpoint is still being built. When off, fall back to
  // plain textareas; when on, surface accept/dismiss suggestion cards.
  const narrativeNlpEnabled = readEnv().narrativeNlp;

  const acceptHpo = (a: NarrativeAnnotation): void => {
    createFeature.mutate(defaultFeatureInput({ id: a.termId, label: a.label }));
  };
  // OMIM accept is a no-op until the diagnoses POST endpoint lands (PTV2-998
  // ships GET / PUT / DELETE; create is the follow-up). Acceptance becomes a
  // dismissal hint so the suggestion doesn't reappear immediately.
  const acceptOmim = (_a: NarrativeAnnotation): void => {
    /* no-op: diagnoses POST endpoint not yet wired */
  };

  const initial = useMemo<NarrativeDraft>(
    () => ({
      indicationForReferral: history.indicationForReferral ?? '',
      medicalAndDevelopmentalHistory:
        history.medicalAndDevelopmentalHistory ?? '',
    }),
    [history],
  );

  // PTV2-1327: the primitive owns the draft — reseed-safety, auto-registration, and autosave (this is
  // an existing record being edited) all come for free. `changedFields` shows only field NAMES (never
  // the free-form clinical prose) in the unsaved-changes prompt.
  const form = useEditableDraft<NarrativeDraft>({
    initial,
    label: 'Clinical narrative',
    autosave: true,
    changedFields: (d, base) => {
      const names: string[] = [];
      if (d.indicationForReferral !== base.indicationForReferral) {
        names.push(t('patient.medicalHistory.indicationForReferral'));
      }
      if (d.medicalAndDevelopmentalHistory !== base.medicalAndDevelopmentalHistory) {
        names.push(t('patient.medicalHistory.history'));
      }
      return names;
    },
    onSave: (d) =>
      updateNarrative.mutateAsync({
        indicationForReferral: nullable(d.indicationForReferral),
        medicalAndDevelopmentalHistory: nullable(d.medicalAndDevelopmentalHistory),
      }),
  });
  const { draft } = form;

  const summaryRows: CollapsibleCardSummaryRow[] = [
    {
      label: t('patient.medicalHistory.indicationForReferral'),
      value: initial.indicationForReferral.length > 0 ? initial.indicationForReferral : '—',
      filled: initial.indicationForReferral.length > 0,
    },
    {
      label: t('patient.medicalHistory.history'),
      value:
        initial.medicalAndDevelopmentalHistory.length > 0
          ? initial.medicalAndDevelopmentalHistory
          : '—',
      filled: initial.medicalAndDevelopmentalHistory.length > 0,
    },
  ];

  return (
    <CollapsibleCard
      idPrefix="medical-history"
      anchorId="narrative"
      title={t('patient.medicalHistory.narrativeHeading')}
      defaultOpen
      summary={summaryRows}
    >
      <div ref={form.containerProps.ref} className="space-y-4">
        {narrativeNlpEnabled ? (
          <NarrativeWithSuggestions
            id="medical-history-indication"
            label={t('patient.medicalHistory.indicationForReferral')}
            placeholder={t('patient.medicalHistory.indicationPlaceholder')}
            value={draft.indicationForReferral}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, indicationForReferral: next }));
            }}
            guid={guid}
            onAcceptHpo={acceptHpo}
            onAcceptOmim={acceptOmim}
          />
        ) : (
          <NarrativeTextarea
            id="medical-history-indication"
            label={t('patient.medicalHistory.indicationForReferral')}
            placeholder={t('patient.medicalHistory.indicationPlaceholder')}
            value={draft.indicationForReferral}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, indicationForReferral: next }));
            }}
          />
        )}
        {narrativeNlpEnabled ? (
          <NarrativeWithSuggestions
            id="medical-history-history"
            label={t('patient.medicalHistory.history')}
            placeholder={t('patient.medicalHistory.historyPlaceholder')}
            value={draft.medicalAndDevelopmentalHistory}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, medicalAndDevelopmentalHistory: next }));
            }}
            guid={guid}
            onAcceptHpo={acceptHpo}
            onAcceptOmim={acceptOmim}
          />
        ) : (
          <NarrativeTextarea
            id="medical-history-history"
            label={t('patient.medicalHistory.history')}
            placeholder={t('patient.medicalHistory.historyPlaceholder')}
            value={draft.medicalAndDevelopmentalHistory}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, medicalAndDevelopmentalHistory: next }));
            }}
          />
        )}
        <DraftFooter form={form} label="Clinical narrative" />
      </div>
    </CollapsibleCard>
  );
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

interface NarrativeTextareaProps {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
}

function NarrativeTextarea({
  id,
  label,
  placeholder,
  value,
  onChange,
}: NarrativeTextareaProps): ReactElement {
  return (
    <div className="space-y-1.5">
      <PhiHintLabel htmlFor={id} label={label} />
      <textarea
        id={id}
        value={value}
        placeholder={placeholder}
        rows={3}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm leading-relaxed resize-y min-h-[80px]"
      />
    </div>
  );
}
